/**
 * Where an assistant reaches keepr from, and what the Connect page hands out
 * for each route (the owner's assistant-channels ruling, 2026-10-06: three
 * routes, nothing to download, no key in a connection flow). Every address,
 * command and sentence a person copies off that page comes from here, so a
 * moved connector or a renamed marketplace is a one-line change.
 */

/** The hosted API, as the skill and the connector are published from it. */
export const KEEPR_API_ORIGIN = 'https://api.keepr.cloud';

/** The public documentation index, the skill and the write contract. */
export const API_DOCS_URL = `${KEEPR_API_ORIGIN}/api/docs`;
export const SKILL_URL = `${API_DOCS_URL}/skill`;
export const CONTRACT_URL = `${API_DOCS_URL}/contract`;

/** The one sentence any shell agent can be told. */
export const SKILL_INSTALL_SENTENCE = `Add the skill defined at ${SKILL_URL}`;

/** The keepr plugin's marketplace: a GitHub repository, as Claude names one. */
export const PLUGIN_MARKETPLACE = 'keeprcloud/keepr-agent';

/**
 * The keepr plugin in Claude Code, two commands. It carries the skill, the
 * connector and keepr's local file tools; `/mcp` then signs the connector in.
 */
export const CLAUDE_CODE_COMMANDS = [
	`claude plugin marketplace add ${PLUGIN_MARKETPLACE}`,
	'claude plugin install keepr@keepr-agent',
];

/**
 * The plugin's update, by hand: refresh the marketplace, then the plugin.
 * What Claude runs when asked to "update the keepr plugin"; a person who
 * turns on the marketplace's auto-update never needs it.
 */
export const PLUGIN_UPDATE_COMMANDS = [
	'claude plugin marketplace update keepr-agent',
	'claude plugin update keepr@keepr-agent',
];

/**
 * The script's own sign-in (keepr-api skills/keepr/scripts/keepr.py,
 * cmd_login, since KPR-249): it opens keepr's page in the browser, the
 * person presses Allow, and the connection is listed under Connected
 * assistants as "keepr.py on <host>". No secret is typed, so the agent may
 * start it; the person still decides on keepr's page.
 */
export const LOGIN_COMMAND = 'python3 scripts/keepr.py login';

/**
 * The developer's key path: run by the person, in their own terminal. It
 * prompts without echo and refuses piped input, which is what keeps the key
 * out of the chat.
 */
export const LOGIN_KEY_COMMAND = 'python3 scripts/keepr.py login --key';

/**
 * The remote connector (Decision 7). Not a key: the consent screen is. Its
 * own host since KPR-245 (the address a Claude directory listing fixes);
 * `${KEEPR_API_ORIGIN}/mcp` is the same connector and keeps working for
 * connections made there before 2026-10-06, but nothing hands it out now.
 */
export const MCP_CONNECTOR_ORIGIN = 'https://mcp.keepr.cloud';
export const MCP_CONNECTOR_URL = `${MCP_CONNECTOR_ORIGIN}/mcp`;

/**
 * The user guide that says how each way of connecting stays current. Every
 * "How to update" link lands on one of its sections; the anchors live beside
 * the channel names in src/utils/agentClients.js. It is also where "Moving
 * from an older setup" goes (the desktop extension, an uploaded skill, a key
 * in Claude Code).
 */
export const UPDATE_GUIDE_PATH = '/docs/guides/assistants/update-your-assistant';

/**
 * keepr's listing in Claude's directory, once there is one (ruling d09): the
 * Claude route then leads with one "Add keepr to Claude" button that opens
 * it, and keeps the connector address as the fallback for plans without the
 * directory. Unset — the case until the listing is live — the route gives
 * the address. Set it at build time as REACT_APP_CLAUDE_DIRECTORY_URL.
 *
 * Only an https address on Claude's own sites is taken: anything else reads
 * as unset, so a typo can never send a person somewhere else.
 */
export function claudeDirectoryUrl(raw = process.env.REACT_APP_CLAUDE_DIRECTORY_URL) {
	const text = String(raw || '').trim();
	if (!text) return null;
	let url;
	try { url = new URL(text); } catch { return null; }
	if (url.protocol !== 'https:') return null;
	const host = url.hostname.toLowerCase();
	const claudeSite = ['claude.ai', 'claude.com'].some(site => host === site || host.endsWith(`.${site}`));
	return claudeSite ? url.toString() : null;
}

export const CLAUDE_DIRECTORY_URL = claudeDirectoryUrl();
